import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template';
import Component from '@glimmer/component';

import $ from 'jquery';

import type { Service } from 'webapp-types';
import type { BillingFeature } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { capitalize } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import onWindowEvents from 'webapp/modifiers/on-window-events';
import CommonRequestTrialButtonComponent from 'webapp/components/common/request-trial-button';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import CommonUpgradePlanButtonComponent from 'webapp/components/common/upgrade-plan-button';

import type BillingPlan from 'webapp/models-custom/billing-plan';

export interface NoResultsBaseArgs {
	onCreate: () => void;
}

/**
 * @deprecated USE /empty-screens/base INSTEAD
 *
 * *************************************************
 * *                                               *
 * *  DEPRECATED USE /empty-screens/base INSTEAD   *
 * *                                               *
 * *************************************************
 */
export default abstract class NoResultsBaseComponent<
	T extends NoResultsBaseArgs,
> extends Component<T> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('flow/promotions')
	declare flowPromotionsService: Service.Flow.Promotions;

	classNames!: string; // string of classes, e.g. 'view-boards flex-item flex-item--centered'

	// defining how actions looks like
	// which buttons are shown
	readMoreUrl!: string; // example 'https://help.databox.com';

	additionalInformationUrl!: string; // example 'https://help.databox.com';

	titleText!: string;
	descText!: string; // description

	// smaller image on top
	topImageLightUrl!: string;
	topImageDarkUrl!: string;
	topImageHeight = 48; // numeric height

	// bigger image next to description
	imageLightUrl!: string;
	imageDarkUrl!: string;
	imageHeight!: number;

	// video specs
	videoUrl!: string; // video url
	videoLength!: string; // string length, e.g. '1:12'

	trialFeature!: BillingFeature;

	_noResultImageEl!: JQuery;

	trackingOrigin!: string;

	get topImageUrl(): string {
		const topImageDarkUrl = this.topImageDarkUrl || this.topImageLightUrl;

		return this.navigationService.theme.isDark ? topImageDarkUrl : this.topImageLightUrl;
	}

	get imageUrl(): string {
		const imageDarkUrl = this.imageDarkUrl || this.imageLightUrl;

		return this.navigationService.theme.isDark ? imageDarkUrl : this.imageLightUrl;
	}

	get mustUpgrade(): boolean {
		return false;
	}

	get payingTrialPromotions() {
		const targetPlan = this.targetPlan,
			payingTrialPromotions = targetPlan
				? this.flowPromotionsService.getHigherPlanAutomatedTrialOptionPromotionsForPlanType(
						targetPlan.planType,
					)
				: [];

		return payingTrialPromotions;
	}

	get isRequestTrialButtonShown(): boolean {
		return this.billingService.canRequestTrial || !!this.payingTrialPromotions.length;
	}

	// defined create btn
	get createButtonText(): string {
		return '';
	}

	get createButtonClass(): string {
		return '';
	}

	get createButtonIcon(): IconName | undefined {
		return 'Plus';
	}

	get createButtonLink(): string {
		return '';
	}

	get containerHeightStyle(): SafeString {
		return htmlSafe(`min-height:${this.imageHeight}px`);
	}

	get showReadMore(): boolean {
		return (
			!!this.readMoreUrl &&
			!this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins &&
			(!this.mustUpgrade || !this.isRequestTrialButtonShown)
		);
	}

	get showReadMoreOnTop(): boolean {
		return (
			!!this.readMoreUrl &&
			!this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins &&
			this.mustUpgrade &&
			this.isRequestTrialButtonShown
		);
	}

	get methodologyLink(): {
		isShown: boolean;
		link: string;
		text: string;
		extraClasses?: string;
	} | null {
		return null;
	}

	get showDemoButton(): boolean {
		return this.accountService.isDemoButtonShown;
	}

	get targetPlan(): BillingPlan | null {
		return this.billingService.getNextPlanForFeatureWithSameBillingCycle(this.trialFeature);
	}

	get targetPlanCapitalized(): string {
		return capitalize(this.targetPlan?.planType || '');
	}

	get isCurrentUserViewer(): boolean {
		return !!this.currentUserService.model?.isViewer;
	}

	get isNewSectionShown() {
		return !this.isCurrentUserViewer;
	}

	get isBookACallButtonShown(): boolean {
		return (
			!this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins &&
			!this.currentUserService.isClientUserInPaidClientSpace
		);
	}

	@action
	calculateInitialNoResultImageWrapperWidth(el: HTMLElement): void {
		this._noResultImageEl = $(el).find('.noresult-image');

		this.recalculateNoResultImageWrapperWidth();
	}

	@action
	recalculateNoResultImageWrapperWidth(): void {
		try {
			if (!this._noResultImageEl.length) {
				return;
			}

			// all the way to the edge
			const imgEl = this._noResultImageEl,
				imgPadding = parseInt(imgEl.css('padding-left'), 10),
				imgOffset = imgEl.offset()?.left || 0,
				newWidth = $(window).width()! - imgPadding - imgOffset;

			$('#fixed-wrapper').width(newWidth);
		} catch (_e) {
			// skip
		}
	}

	@action
	create(): void {
		this.args.onCreate();
	}

	@action
	watchVideo(): void {
		if (this.videoUrl) {
			this.navigationService.openVideoPopup(this.videoUrl);
		}
	}

	@action
	openDemoAccount() {
		void this.accountService.redirectToDemoAccount(this.trackingOrigin);
	}

	@action
	openBookACall(): void {
		this.analyticsService.event(TrackingEvent.CALL_INITIATED, {
			origin: this.trackingOrigin,
		});
		this.accountService.requestSetupHelpAlternative();
	}
	<template>
		<div
			id="noResultsWide"
			class={{this.classNames}}
			style={{this.containerHeightStyle}}
			{{didInsert this.calculateInitialNoResultImageWrapperWidth}}
			{{onWindowEvents this.recalculateNoResultImageWrapperWidth "resize" 150}}
		>
			<div class="noresult-content">
				{{#if this.topImageUrl}}
					<div class="topimg">
						<img src={{this.topImageUrl}} height={{this.topImageHeight}} alt />
					</div>
				{{/if}}
				<div>
					<strong>{{{this.titleText}}}</strong>
					<p>
						{{{this.descText}}}
						{{#if this.showReadMoreOnTop}}
							<a href={{this.readMoreUrl}} target="_blank">Read more</a>
						{{/if}}
					</p>

					{{#unless this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins}}
						{{#if this.videoUrl}}
							<a role="button" class="tutorial" {{on "click" this.watchVideo}}>
								<CommonUiIconComponent @name="Play" @size="md" />
								Watch demo video
								{{#if this.videoLength}} ({{this.videoLength}}){{/if}}
							</a>
						{{/if}}
					{{/unless}}

					{{#if this.showReadMore}}
						<a role="button" class="tutorial" href={{this.readMoreUrl}} target="_blank">
							<CommonUiIconComponent @name="Book" @size="md" />
							Learn more
						</a>
					{{/if}}

					{{#if this.isBookACallButtonShown}}
						<a role="button" class="tutorial" {{on "click" this.openBookACall}}>
							<CommonUiIconComponent @name="Assistance" @size="md" />
							Get assistance
						</a>
					{{/if}}

					<div class="actions">

						{{#if this.mustUpgrade}}

							{{#if this.isRequestTrialButtonShown}}
								<CommonRequestTrialButtonComponent
									@feature={{this.trialFeature}}
									@size="big"
								/>
							{{else}}
								<CommonUpgradePlanButtonComponent
									@feature={{this.trialFeature}}
									@text={{if this.createButtonText this.createButtonText "New"}}
									@icon="Plus"
									@size="big"
								/>
							{{/if}}

						{{else}}

							{{#if this.createButtonText}}
								{{#if this.createButtonLink}}
									<a
										class="button size-big {{this.createButtonClass}}"
										href={{this.createButtonLink}}
										target="_blank"
									>
										{{#if this.createButtonIcon}}
											<CommonUiIconComponent
												@name={{this.createButtonIcon}}
												@size="md"
											/>
										{{/if}}
										{{this.createButtonText}}
									</a>
								{{else}}
									<a
										role="button"
										class="button size-big {{this.createButtonClass}}"
										{{on "click" this.create}}
									>
										{{#if this.createButtonIcon}}
											<CommonUiIconComponent
												@name={{this.createButtonIcon}}
												@size="md"
											/>
										{{/if}}
										{{this.createButtonText}}
									</a>
								{{/if}}
							{{/if}}

						{{/if}}

						{{#if this.showDemoButton}}
							<div class="demo-link">
								or explore in&nbsp;
								<a role="button" {{on "click" this.openDemoAccount}}>
									Databox demo account
									<CommonUiIconComponent @name="External" @size="sm" />
								</a>
							</div>
						{{/if}}

					</div>
				</div>
			</div>

			{{#if this.imageUrl}}
				<div class="noresult-image">
					<div id="fixed-wrapper">
						<img src={{this.imageUrl}} height={{this.imageHeight}} alt />
					</div>
				</div>
			{{/if}}
		</div>
	</template>
}
